import { Component, computed, inject, signal, WritableSignal } from '@angular/core';
import { TAGFinanceTransaction } from '../../../models/tagfinances';
import { TAGUser } from '../../../models/taguser';
import { TAGEvent } from '../../../models/tagevent';
import { ApiService } from '../../api.service';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FluidModule } from 'primeng/fluid';
import { environment } from '../../../environments/environment';
import {
  newProgressDialogStep,
  ProgressDialogStep,
  ProgressDialogStepState,
  openProgressDialog,
} from '../../components/progressDialog/progressDialog';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { AutoCompleteCompleteEvent } from 'primeng/types/autocomplete';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputNumberModule } from 'primeng/inputnumber';
import { DocumentGridComponent } from '../../components/document-grid/document-grid';

export interface FinanceTransactionEditDialogData {
  transaction: TAGFinanceTransaction;
}

@Component({
  selector: 'int-finance-transactioneditdialog',
  imports: [
    FluidModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    FloatLabelModule,
    SkeletonModule,
    DatePickerModule,
    ButtonModule,
    TooltipModule,
    ToggleButtonModule,
    AutoCompleteModule,
    TranslatePipe,
    DocumentGridComponent,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: 'financeTransactionEditDialog.html',
  styleUrl: './financeTransactionEditDialog.scss',
})
export class FinanceTransactionEditDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private api = inject(ApiService);

  transactionForm = new FormGroup({
    isIncome: new FormControl(false),
    pending: new FormControl(false),
    reason: new FormControl('', [Validators.required]),
    date: new FormControl(new Date(), [Validators.required]),
    notes: new FormControl(''),
    amount: new FormControl(0),
  });

  userControl = new FormControl<TAGUser>(undefined);
  eventControl = new FormControl<TAGEvent>(undefined);

  transactionIsNew: boolean = true;
  transaction: TAGFinanceTransaction = new TAGFinanceTransaction();

  users = signal<TAGUser[]>([]);
  loadingUsers = signal(true);
  filteredUsers = signal<TAGUser[]>([]);
  events = signal<TAGEvent[]>([]);
  loadingEvents = signal(true);
  filteredEvents = signal<TAGEvent[]>([]);

  newFiles = signal<{ fileName: string; mimeType: string; fileData: string }[]>([]);
  deletedFiles = signal<string[]>([]);

  allFiles = computed(() => {
    let all = this.transaction.files.concat(this.newFiles());
    all = all.filter((f) => !this.deletedFiles().includes(f.fileName));
    console.log(all);
    return all;
  });

  get apiUrl() {
    return environment.apiUrl;
  }

  constructor(
    public dialogRef: DynamicDialogRef<FinanceTransactionEditDialog>,
    public config: DynamicDialogConfig<FinanceTransactionEditDialogData>,
  ) {
    if (config.data?.transaction) {
      this.transaction = new TAGFinanceTransaction(
        Object.assign({}, config.data.transaction.serialize()),
      );
      this.transactionForm.patchValue(this.transaction);
    } else {
      this.transaction = new TAGFinanceTransaction();
      this.transactionForm.reset();
    }
    this.transactionIsNew = this.transaction._id == undefined;

    if (this.transaction._id) {
      config.header = this.translate.translate('internal.finances.transactionEdit.title')();
    } else {
      config.header = this.translate.translate('internal.finances.transactionEdit.titleNew')();
    }
  }

  ngOnInit(): void {
    this.api.waitForAPI().then(() => {
      this.api
        .getUsers()
        .then((users: TAGUser[]) => {
          this.users.set(users);
          this.userControl.setValue(users.find((u) => u.username === this.transaction.user));
          this.loadingUsers.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate("errors.couldNotLoadUsers")(),
            detail: reason.message,
          });
        });

      this.api
        .getEvents()
        .then((events: TAGEvent[]) => {
          this.events.set(events);
          this.eventControl.setValue(events.find((e) => e._id === this.transaction.event));
          this.loadingEvents.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate("errors.couldNotLoadEvents")(),
            detail: reason.message,
          });
        });
    });
  }

  _searchUser(event: AutoCompleteCompleteEvent) {
    this.filteredUsers.set(
      this.users().filter(
        (user) =>
          user.username.toLowerCase().includes(event.query.toLowerCase()) ||
          user.firstname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.lastname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.fullname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.email.toLowerCase().includes(event.query.toLowerCase()),
      ),
    );

    console.log(event.query, this.filteredUsers());
  }

  _searchEvent(event: AutoCompleteCompleteEvent) {
    this.filteredEvents.set(
      this.events().filter((evt) => evt.name.toLowerCase().includes(event.query.toLowerCase())),
    );

    console.log(event.query, this.filteredEvents());
  }

  uploadFile(event) {
    console.log(event);
    this.newFiles.update((f) => [...f, event]);
  }

  deleteFile(fileName: string) {
    if (this.newFiles().find((nf) => nf.fileName === fileName)) {
      this.newFiles.update((files) => files.filter((f) => f.fileName !== fileName));
    } else {
      this.deletedFiles.update((f) => [...f, fileName]);
    }
  }

  closeDialog(save?: boolean) {
    if (!save) {
      this.dialogRef.close(false);
      return;
    }

    if (!this.transactionForm.valid) return;

    let updateStep = newProgressDialogStep(
      new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate("internal.finances.transactionEdit.updateProgress")()),
    );

    if (this.transactionIsNew) {
      updateStep = newProgressDialogStep(
        new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate("internal.finances.transactionEdit.createProgress")()),
      );
    }

    let uploadStep = newProgressDialogStep(
      new ProgressDialogStep(ProgressDialogStepState.WAITING, this.translate.translate("internal.finances.transactionEdit.uploadFilesProgress")())
        .setShowProgress(true)
        .setProgress(0),
    );

    if (this.newFiles().length == 0) {
      uploadStep.update((s) => s.setState(ProgressDialogStepState.SKIPPED));
    }

    let progressDialog = openProgressDialog(this.dialogService, [updateStep, uploadStep]);

    progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
      updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      this.transaction.deserialize(this.transactionForm.value);
      if (this.userControl.value != undefined)
        this.transaction.user = this.userControl.value.username;
      if (this.eventControl.value != undefined)
        this.transaction.event = this.eventControl.value._id;

      if (this.transactionIsNew) {
        this.api
          .newFinanceTransaction(this.transaction)
          .then(async (newTransaction: TAGFinanceTransaction) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));

            // Update Files of Transaction
            if (this.newFiles().length > 0 || this.deletedFiles().length > 0) {
              this.saveFiles(newTransaction._id, uploadStep)
                .then(async () => {
                  await pDialog.finishDialog();
                  this.dialogRef.close(true);
                })
                .catch((reason) => {
                  pDialog.finishDialog(-1);
                  console.log(reason);
                });
            } else {
              await pDialog.finishDialog();
              this.dialogRef.close(true);
            }
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .updateFinanceTransaction(this.transaction)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));

            // Update Files of Transaction
            if (this.newFiles().length > 0 || this.deletedFiles().length > 0) {
              this.saveFiles(this.transaction._id, uploadStep)
                .then(async () => {
                  await pDialog.finishDialog();
                  this.dialogRef.close(true);
                })
                .catch((reason) => {
                  pDialog.finishDialog(-1);
                  console.log(reason);
                });
            } else {
              await pDialog.finishDialog();
              this.dialogRef.close(true);
            }
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }

  saveFiles(transactionID: string, uploadStep: WritableSignal<ProgressDialogStep>) {
    return new Promise<void>(async (resolve, reject) => {
      uploadStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      let allFiles = this.newFiles().length;
      let doneFiles = 0;

      for (let f of this.newFiles()) {
        try {
          await this.api.uploadFinanceTransactionAttachment(
            transactionID,
            f.fileName,
            f.fileData,
            (progress) => {
              uploadStep.update((s) => s.setProgress(doneFiles / allFiles + progress / allFiles));
            },
          );

          doneFiles++;
        } catch (reason) {
          console.log(reason);
          uploadStep.update((s) =>
            s.setState(ProgressDialogStepState.ERROR).setSubtitle(reason.message),
          );
          return reject(reason);
        }
      }

      for (let f of this.deletedFiles()) {
        try {
          await this.api.deleteFinanceTransactionAttachment(transactionID, f);
        } catch (reason) {
          console.log(reason);
          uploadStep.update((s) =>
            s.setState(ProgressDialogStepState.ERROR).setSubtitle(reason.message),
          );
          return reject(reason);
        }
      }

      uploadStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
    });
  }
}
